import { CalendarClock, Mail, Save, Search, ShieldAlert } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "../../components/PageHeader";
import {
  getAdminSchedulerData,
  sendAdminSchedulerLink,
  updateAdminSchedulerRecord,
  updateAdminSchedulerSettings,
} from "../../lib/api";
import { packageCatalog, selectableServices } from "../../lib/proposalCatalog";
import { schedulerRequirementOptions, schedulerStatusOptions, schedulerTypes } from "../../lib/schedulerRules";
import type { SchedulerDashboardStats, SchedulerRecord, SchedulerRequirement, SchedulerSettingRecord, SchedulerStatus } from "../../lib/types";

const buttonClass = "focus-ring inline-flex items-center justify-center gap-2 rounded-full bg-ktd-black px-4 py-2 text-xs font-extrabold text-ktd-softGold transition hover:bg-[#1f1a12]";
const quietButtonClass = "focus-ring inline-flex items-center justify-center gap-2 rounded-full border border-ktd-gold/30 bg-white px-4 py-2 text-xs font-extrabold text-ktd-rich transition hover:border-ktd-gold hover:bg-ktd-cream";

export function AdminSchedulerPage() {
  const [settings, setSettings] = useState<SchedulerSettingRecord[]>([]);
  const [records, setRecords] = useState<SchedulerRecord[]>([]);
  const [stats, setStats] = useState<SchedulerDashboardStats | null>(null);
  const [message, setMessage] = useState("");
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    search: "",
    packageId: searchParams.get("packageId") || "",
    schedulerType: searchParams.get("schedulerType") || "",
    status: searchParams.get("status") || "",
  });

  const load = async () => {
    const data = await getAdminSchedulerData();
    setSettings(data.settings);
    setRecords(data.records);
    setStats(data.stats);
  };

  useEffect(() => {
    void load().catch((error) => setMessage(error instanceof Error ? error.message : "Unable to load scheduler workspace."));
  }, []);

  const updateSetting = (schedulerType: string, patch: Partial<SchedulerSettingRecord>) => {
    setSettings((current) => current.map((setting) => setting.schedulerType === schedulerType ? { ...setting, ...patch } : setting));
  };

  const saveSettings = async () => {
    setMessage("");
    try {
      const result = await updateAdminSchedulerSettings(settings);
      setSettings(result.settings);
      setMessage("Scheduler settings saved.");
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to save scheduler settings.");
    }
  };

  const updateRecordLocal = (id: string, patch: Partial<SchedulerRecord>) => {
    setRecords((current) => current.map((record) => record.id === id ? { ...record, ...patch } : record));
  };

  const saveRecord = async (record: SchedulerRecord) => {
    setMessage("");
    try {
      const result = await updateAdminSchedulerRecord(record.id, { ...record });
      updateRecordLocal(record.id, result.record);
      setMessage(`${record.schedulerLabel} updated for ${record.clientName}.`);
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to update scheduler record.");
    }
  };

  const sendLink = async (record: SchedulerRecord) => {
    setMessage("");
    try {
      const result = await sendAdminSchedulerLink(record.id);
      updateRecordLocal(record.id, result.record);
      setMessage(result.message);
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to send scheduling link.");
    }
  };

  const filteredRecords = useMemo(() => records.filter((record) => {
    const haystack = [record.clientName, record.clientEmail, record.eventName, packageLabel(record.selectedPackage), record.schedulerLabel, record.status].join(" ").toLowerCase();
    return (!filters.search || haystack.includes(filters.search.toLowerCase()))
      && (!filters.packageId || record.selectedPackage === filters.packageId)
      && (!filters.schedulerType || record.schedulerType === filters.schedulerType)
      && (!filters.status || record.status === filters.status);
  }), [records, filters]);

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow="Scheduling"
        title="Manage client scheduling links, call requirements, and kickoff readiness."
        subtitle="Use external links for now, while keeping the workflow ready for a future native ClientFlow Studio scheduler."
      />

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <StatCard label="Discovery Calls Scheduled" value={stats?.discoveryCallsScheduled || 0} />
        <StatCard label="Kickoff Calls Scheduled" value={stats?.kickoffCallsScheduled || 0} />
        <StatCard label="Kickoff Calls Completed" value={stats?.kickoffCallsCompleted || 0} />
        <StatCard label="Revision Calls Scheduled" value={stats?.revisionCallsScheduled || 0} />
        <StatCard label="Final Review Calls Scheduled" value={stats?.finalReviewCallsScheduled || 0} />
      </section>

      {message && <p className="ivory-card rounded-lg p-4 text-sm font-bold text-ktd-rich">{message}</p>}

      <section className="ivory-card rounded-lg p-6 sm:p-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <SectionTitle title="Scheduler Settings" compact />
          <button onClick={() => void saveSettings()} className={buttonClass}><Save className="size-4" />Save Scheduler Settings</button>
        </div>
        <div className="mt-5 grid gap-4">
          {settings.map((setting) => (
            <SchedulerSettingCard key={setting.schedulerType} setting={setting} onChange={(patch) => updateSetting(setting.schedulerType, patch)} />
          ))}
        </div>
      </section>

      <section className="ivory-card rounded-lg p-6 sm:p-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <SectionTitle title="Scheduling Status" compact />
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <label className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-ktd-muted" />
              <input value={filters.search} onChange={(event) => setFilters({ ...filters, search: event.target.value })} placeholder="Client or event" className="form-field focus-ring rounded-lg py-3 pl-11 pr-4 text-sm font-semibold" />
            </label>
            <select value={filters.packageId} onChange={(event) => setFilters({ ...filters, packageId: event.target.value })} className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold">
              <option value="">All packages</option>
              {packageCatalog.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
            <select value={filters.schedulerType} onChange={(event) => setFilters({ ...filters, schedulerType: event.target.value })} className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold">
              <option value="">All scheduler types</option>
              {schedulerTypes.map((type) => <option key={type.id} value={type.id}>{type.label}</option>)}
            </select>
            <select value={filters.status} onChange={(event) => setFilters({ ...filters, status: event.target.value })} className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold">
              <option value="">All statuses</option>
              {schedulerStatusOptions.map((status) => <option key={status} value={status}>{status}</option>)}
            </select>
          </div>
        </div>

        <div className="mt-5 grid gap-4">
          {filteredRecords.length === 0 ? <p className="rounded-lg border border-ktd-gold/20 bg-white p-4 text-sm font-bold text-ktd-muted">No scheduler records match these filters yet.</p> : null}
          {filteredRecords.map((record) => (
            <SchedulerRecordCard key={record.id} record={record} onChange={(patch) => updateRecordLocal(record.id, patch)} onSave={() => void saveRecord(record)} onSend={() => void sendLink(record)} />
          ))}
        </div>
      </section>
    </div>
  );
}

function SchedulerSettingCard({ setting, onChange }: { setting: SchedulerSettingRecord; onChange: (patch: Partial<SchedulerSettingRecord>) => void }) {
  return (
    <article className="rounded-lg border border-ktd-gold/20 bg-white p-4">
      <div className="grid gap-4 lg:grid-cols-[1fr_1fr_140px] lg:items-end">
        <label className="flex items-center gap-3 text-sm font-extrabold text-ktd-rich">
          <input type="checkbox" checked={setting.enabled} onChange={(event) => onChange({ enabled: event.target.checked })} className="size-5 accent-ktd-gold" />
          <span>
            {setting.label}
            <span className="block text-xs font-bold text-ktd-muted">{schedulerTypes.find((type) => type.id === setting.schedulerType)?.description}</span>
          </span>
        </label>
        <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
          External scheduler link
          <input value={setting.schedulerUrl} onChange={(event) => onChange({ schedulerUrl: event.target.value })} placeholder="https://calendly.com/..." className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold" />
        </label>
        <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
          Duration
          {setting.schedulerType === "kickoff-call" ? (
            <select value={setting.durationMinutes} onChange={(event) => onChange({ durationMinutes: Number(event.target.value) })} className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold">
              <option value={60}>60 minutes</option>
              <option value={90}>90 minutes</option>
            </select>
          ) : (
            <input type="number" min={15} max={180} step={5} value={setting.durationMinutes} onChange={(event) => onChange({ durationMinutes: Number(event.target.value) })} className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold" />
          )}
        </label>
      </div>

      <details className="mt-4 rounded-lg border border-ktd-gold/15 bg-ktd-cream/45 p-4">
        <summary className="cursor-pointer text-sm font-black text-[#8a6c14]">Package and service requirement rules</summary>
        <div className="mt-4 grid gap-5 xl:grid-cols-2">
          <RuleGrid title="Package Rules" items={packageCatalog} rules={setting.packageRules} onChange={(rules) => onChange({ packageRules: rules })} />
          <RuleGrid title="Service Rules" items={selectableServices} rules={setting.serviceRules} onChange={(rules) => onChange({ serviceRules: rules })} />
        </div>
        <label className="mt-4 grid gap-2 text-sm font-extrabold text-ktd-rich">
          Internal notes
          <textarea value={setting.notes} onChange={(event) => onChange({ notes: event.target.value })} rows={3} className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold" />
        </label>
      </details>
    </article>
  );
}

function SchedulerRecordCard({ record, onChange, onSave, onSend }: { record: SchedulerRecord; onChange: (patch: Partial<SchedulerRecord>) => void; onSave: () => void; onSend: () => void }) {
  return (
    <article className="rounded-lg border border-ktd-gold/20 bg-white p-4">
      <div className="grid gap-4 xl:grid-cols-[1fr_1.2fr_auto] xl:items-start">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-display text-2xl font-bold text-ktd-rich">{record.clientName}</h3>
            <Badge label={record.requirement} />
            {record.productionBlocked ? <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-3 py-1 text-xs font-black text-red-700"><ShieldAlert className="size-3" />Production blocked</span> : null}
          </div>
          <p className="mt-1 text-sm font-semibold text-ktd-muted">{record.eventName || "No event name"} · {packageLabel(record.selectedPackage)}</p>
          <p className="mt-1 text-sm font-black text-[#8a6c14]">{record.schedulerLabel}</p>
          {record.schedulerUrl ? <a href={record.schedulerUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex text-sm font-black text-[#8a6c14] underline">Open scheduler link</a> : null}
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <SelectControl label="Requirement" value={record.requirement} options={schedulerRequirementOptions} onChange={(value) => onChange({ requirement: value as SchedulerRequirement })} />
          <SelectControl label="Status" value={record.status} options={schedulerStatusOptions} onChange={(value) => onChange({ status: value as SchedulerStatus })} />
          <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
            Duration
            <input type="number" min={15} max={180} value={record.durationMinutes} onChange={(event) => onChange({ durationMinutes: Number(event.target.value) })} className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold" />
          </label>
          <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
            Scheduled for
            <input type="datetime-local" value={record.scheduledFor} onChange={(event) => onChange({ scheduledFor: event.target.value })} className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold" />
          </label>
          <label className="grid gap-2 text-sm font-extrabold text-ktd-rich md:col-span-2">
            Scheduler link override
            <input value={record.schedulerUrl} onChange={(event) => onChange({ schedulerUrl: event.target.value })} className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold" />
          </label>
          <label className="flex items-center gap-3 text-sm font-extrabold text-ktd-rich">
            <input type="checkbox" checked={record.manualOverride} onChange={(event) => onChange({ manualOverride: event.target.checked })} className="size-5 accent-ktd-gold" />
            Manual production override
          </label>
          <label className="grid gap-2 text-sm font-extrabold text-ktd-rich md:col-span-2">
            Override notes
            <textarea value={record.overrideNotes} onChange={(event) => onChange({ overrideNotes: event.target.value })} rows={2} className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold" />
          </label>
        </div>

        <div className="flex flex-wrap gap-2 xl:justify-end">
          <button onClick={onSend} className={quietButtonClass}><Mail className="size-4" />Resend Link</button>
          <button onClick={onSave} className={buttonClass}><Save className="size-4" />Save Status</button>
        </div>
      </div>
    </article>
  );
}

function RuleGrid({ title, items, rules, onChange }: { title: string; items: Array<{ id: string; name: string }>; rules: Record<string, SchedulerRequirement>; onChange: (rules: Record<string, SchedulerRequirement>) => void }) {
  return (
    <div>
      <h3 className="text-sm font-black uppercase tracking-[0.12em] text-ktd-muted">{title}</h3>
      <div className="mt-3 grid gap-3">
        {items.map((item) => (
          <label key={item.id} className="grid gap-2 rounded-lg bg-white p-3 text-sm font-extrabold text-ktd-rich">
            {item.name}
            <select value={rules[item.id] || "Not Needed"} onChange={(event) => onChange({ ...rules, [item.id]: event.target.value as SchedulerRequirement })} className="form-field focus-ring rounded-lg px-3 py-2 text-sm font-semibold">
              {schedulerRequirementOptions.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </label>
        ))}
      </div>
    </div>
  );
}

function SelectControl({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (value: string) => void }) {
  return (
    <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)} className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold">
        {options.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    </label>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <article className="ivory-card rounded-lg p-5">
      <CalendarClock className="size-7 text-[#8a6c14]" />
      <p className="mt-4 text-sm font-bold text-ktd-muted">{label}</p>
      <strong className="mt-2 block font-display text-4xl text-ktd-rich">{value}</strong>
    </article>
  );
}

function Badge({ label }: { label: string }) {
  return <span className="rounded-full border border-ktd-gold/25 bg-ktd-cream px-3 py-1 text-xs font-black text-[#8a6c14]">{label}</span>;
}

function SectionTitle({ title, compact = false }: { title: string; compact?: boolean }) {
  return <h2 className={`${compact ? "" : "mt-7"} font-display text-3xl font-bold text-ktd-rich`}>{title}</h2>;
}

function packageLabel(packageId: string) {
  return packageCatalog.find((item) => item.id === packageId)?.name || "No package selected";
}
